import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Form,
  Badge,
  Modal,
  Pagination,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFolderOpen,
  faFolderTree,
  faCloudArrowDown,
  faArrowRight,
  faFile,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { getDownloadURL, ref } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { addLog, getFilesIndex, storage } from "../../firebase";
import { useAuth } from "../../firebaseContext";
import FileIndexSearch from "../Pages/FileIndexSearch";

const PAGE_SIZE = 9;

export default function Index() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [indexFiles, setIndexFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [showQuickSearch, setShowQuickSearch] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const searchInputRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchFiles() {
      if (isMounted) {
        setLoading(true);
        setError(null);
      }

      try {
        const data = await getFilesIndex();
        const files = data.docs.map((doc) => doc.data());
        files.sort((a, b) =>
          (a.filename || "")
            .toLowerCase()
            .localeCompare((b.filename || "").toLowerCase())
        );

        if (isMounted) {
          setIndexFiles(files);
        }
      } catch (err) {
        console.error("Unable to load file index", err);
        if (isMounted) {
          setIndexFiles([]);
          setError(
            "We couldn't load the file index right now. Please refresh or try again shortly."
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchFiles();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleNavigate = useCallback(
    (folderPath) => {
      if (!folderPath) return;

      const folderArray = folderPath.split("/").filter(Boolean);
      const path = folderArray[folderArray.length - 1];

      if (!path) return;

      if (folderPath.toLowerCase().includes("viewable files")) {
        navigate(`/file/${encodeURIComponent(path)}`, {
          state: { fullPath: folderPath },
          replace: false,
        });
      } else {
        navigate(`/folder/${encodeURIComponent(path)}`, {
          state: { folder: folderPath },
          replace: false,
        });
      }
    },
    [navigate]
  );

  const getReadableLocation = useCallback((location) => {
    if (!location) return "Uncategorised";

    const segments = location.split("/").filter(Boolean);
    if (!segments.length) return "Uncategorised";

    const first = segments[0].toLowerCase();
    const shouldDropFirst =
      /downloadable|viewable/.test(first) && segments.length > 1;
    const cleanedSegments = shouldDropFirst ? segments.slice(1) : segments;

    return cleanedSegments.join(" / ") || segments[segments.length - 1];
  }, []);

  const getFileExtension = useCallback((filename) => {
    if (!filename) return "FILE";
    const parts = filename.split(".");
    return parts.length > 1 ? parts.pop().toUpperCase() : "FILE";
  }, []);

  const filteredFiles = useMemo(() => {
    if (!indexFiles.length) return [];

    const query = searchValue.trim().toLowerCase();
    if (!query) {
      return indexFiles;
    }

    return indexFiles.filter((file) => {
      const name = (file.filename || "").toLowerCase();
      const location = (file.location || "").toLowerCase();
      return name.includes(query) || location.includes(query);
    });
  }, [indexFiles, searchValue]);

  const resultCount = filteredFiles.length;
  const rawTotalPages = Math.ceil(resultCount / PAGE_SIZE);
  const totalPages = Math.max(1, rawTotalPages);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, indexFiles]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const safePage = Math.min(currentPage, totalPages);

  const paginatedFiles = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredFiles.slice(start, start + PAGE_SIZE);
  }, [filteredFiles, safePage]);

  const rangeStart = resultCount === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd =
    resultCount === 0 ? 0 : Math.min(rangeStart + PAGE_SIZE - 1, resultCount);

  const paginationItems = useMemo(() => {
    if (rawTotalPages <= 1) return [];

    if (rawTotalPages <= 7) {
      return Array.from({ length: rawTotalPages }, (_, index) => ({
        type: "page",
        page: index + 1,
      }));
    }

    const candidatePages = new Set([
      1,
      rawTotalPages,
      safePage,
      safePage - 1,
      safePage + 1,
      safePage - 2,
      safePage + 2,
    ]);

    const sorted = Array.from(candidatePages)
      .filter((page) => page >= 1 && page <= rawTotalPages)
      .sort((a, b) => a - b);

    const items = [];
    let previous = 0;
    sorted.forEach((page) => {
      if (page - previous > 1) {
        items.push({ type: "ellipsis" });
      }
      items.push({ type: "page", page });
      previous = page;
    });

    return items;
  }, [rawTotalPages, safePage]);

  const locationStats = useMemo(() => {
    if (!indexFiles.length) {
      return { totalLocations: 0, topLocations: [] };
    }

    const counts = new Map();
    indexFiles.forEach((file) => {
      const key = file.location || "Uncategorised";
      const current = counts.get(key) || 0;
      counts.set(key, current + 1);
    });

    const entries = Array.from(counts.entries())
      .map(([location, count]) => ({
        location,
        count,
        label: getReadableLocation(location),
      }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.label.localeCompare(b.label);
      });

    return {
      totalLocations: entries.length,
      topLocations: entries.slice(0, 6),
    };
  }, [getReadableLocation, indexFiles]);

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const handleDownload = useCallback(
    async (file) => {
      if (!file || !file.location || !file.filename) return;

      setDownloadingFile(file.filename);

      try {
        const fileRef = ref(storage, `${file.location}/${file.filename}`);
        const url = await getDownloadURL(fileRef);

        try {
          await addLog(
            currentUser?.email || "unknown",
            file.filename,
            "Downloaded"
          );
        } catch (logErr) {
          console.warn("Failed to record download in logs", logErr);
        }

        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = file.filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } catch (err) {
        console.error("Download failed", err);
      } finally {
        setDownloadingFile(null);
      }
    },
    [currentUser]
  );

  const totalFiles = indexFiles.length;
  const searchActive = Boolean(searchValue.trim());

  const summaryPrefix = resultCount
    ? `Showing ${rangeStart}–${rangeEnd} of ${resultCount} ${
        searchActive
          ? resultCount === 1
            ? "match"
            : "matches"
          : resultCount === 1
          ? "file"
          : "files"
      }.`
    : searchActive
    ? "No files match your search right now. Try another keyword or clear the filter."
    : "We haven’t indexed any files yet. Upload files to see them appear here automatically.";

  const summarySuffix =
    resultCount && !searchActive
      ? " Type a keyword or folder name to narrow the list instantly."
      : "";

  return (
    <div className="downloads-page index-page">
      <section className="downloads-hero-wrapper py-5">
        <Container fluid="lg">
          <div className="downloads-hero index-hero">
            <Row className="align-items-center gy-4">
              <Col lg={7} className="downloads-hero-content">
                <div className="downloads-hero-badge">File index</div>
                <h1 className="downloads-hero-title">
                  Find every resource in seconds
                </h1>
                <p className="downloads-hero-subtitle">
                  Search across the entire Nobility Care Australia library. Jump
                  straight to a folder, filter by title, or download the exact
                  file you need without leaving this page.
                </p>
                <div className="downloads-hero-actions">
                  <Button
                    variant="light"
                    className="downloads-hero-action"
                    onClick={() => setShowQuickSearch(true)}
                  >
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    Quick search
                  </Button>
                  <Button
                    variant="outline-light"
                    className="downloads-hero-action"
                    onClick={() => navigate("/downloads")}
                  >
                    <FontAwesomeIcon icon={faFolderOpen} className="me-2" />
                    Browse downloads
                  </Button>
                </div>
              </Col>
              <Col lg={5}>
                <Card className="downloads-hero-card index-hero-card h-100">
                  <Card.Body>
                    <Row className="g-3">
                      <Col xs={12} sm={6}>
                        <div className="downloads-stat-card index-stat-card">
                          <div className="downloads-stat-icon index-stat-icon-primary">
                            <FontAwesomeIcon icon={faFile} />
                          </div>
                          <div>
                            <div className="downloads-stat-value">
                              {totalFiles}
                            </div>
                            <div className="downloads-stat-label">
                              Indexed files
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div className="downloads-stat-card index-stat-card">
                          <div className="downloads-stat-icon index-stat-icon-secondary">
                            <FontAwesomeIcon icon={faFolderTree} />
                          </div>
                          <div>
                            <div className="downloads-stat-value">
                              {locationStats.totalLocations}
                            </div>
                            <div className="downloads-stat-label">
                              Unique locations
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12}>
                        <div className="downloads-tip-card index-tip-card">
                          <h3 className="downloads-tip-title">
                            Need it faster?
                          </h3>
                          <p className="downloads-tip-copy">
                            Launch the quick search panel or open a highlighted
                            folder below to reach your files in just a few
                            clicks.
                          </p>
                          <Button
                            variant="outline-primary"
                            className="downloads-tip-btn"
                            onClick={() => setShowQuickSearch(true)}
                          >
                            Launch quick search
                            <FontAwesomeIcon
                              icon={faArrowRight}
                              className="ms-2"
                            />
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      <Container fluid="lg" className="downloads-content pb-5">
        {error && (
          <Alert variant="danger" className="downloads-alert">
            {error}
          </Alert>
        )}

        <section className="downloads-section mb-4">
          <div className="downloads-section-header">
            <div>
              <h2 className="downloads-section-title">Highlighted folders</h2>
              <p className="downloads-section-subtitle">
                Popular locations from the index, ranked by how many files they
                contain.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="downloads-loading-panel">
              <Spinner animation="border" role="status" />
            </div>
          ) : locationStats.topLocations.length > 0 ? (
            <Row className="g-4">
              {locationStats.topLocations.map((item) => (
                <Col key={item.location} xs={12} md={6} xl={4}>
                  <Card
                    className="downloads-folder-card index-location-card h-100"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNavigate(item.location)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleNavigate(item.location);
                      }
                    }}
                  >
                    <Card.Body className="downloads-folder-body">
                      <div className="downloads-folder-icon">
                        <FontAwesomeIcon icon={faFolderOpen} />
                      </div>
                      <div className="index-location-details">
                        <h3
                          className="downloads-folder-name mb-1"
                          title={item.label}
                        >
                          {item.label}
                        </h3>
                        <p className="index-location-count">
                          {item.count} {item.count === 1 ? "file" : "files"}
                        </p>
                      </div>
                      <div className="downloads-folder-arrow">
                        <FontAwesomeIcon icon={faArrowRight} />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Alert variant="light" className="downloads-empty">
              We haven&apos;t indexed any folders yet. New locations will appear
              here automatically once files are uploaded.
            </Alert>
          )}
        </section>

        <section className="downloads-section">
          <div className="downloads-section-header flex-column flex-lg-row align-items-lg-center">
            <div>
              <h2 className="downloads-section-title">Browse the full index</h2>
              <p className="downloads-section-subtitle">
                {summaryPrefix}
                {summarySuffix}
              </p>
            </div>
            <Form
              className="downloads-search-form mt-3 mt-lg-0"
              onSubmit={(event) => event.preventDefault()}
            >
              <div className="downloads-search-input">
                <FontAwesomeIcon icon={faSearch} />
                <Form.Control
                  type="search"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder="Search by file or folder"
                  ref={searchInputRef}
                />
                {searchValue && (
                  <Button
                    type="button"
                    variant="link"
                    className="downloads-search-clear"
                    onClick={handleClearSearch}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                    <span className="visually-hidden">Clear search</span>
                  </Button>
                )}
              </div>
            </Form>
          </div>

          {loading ? (
            <div className="downloads-loading-panel">
              <Spinner animation="border" role="status" />
            </div>
          ) : paginatedFiles.length > 0 ? (
            <Row className="g-4">
              {paginatedFiles.map((file) => (
                <Col
                  key={`${file.location}-${file.filename}`}
                  xs={12}
                  md={6}
                  xl={4}
                >
                  <Card className="downloads-file-card index-file-card h-100">
                    <Card.Body>
                      <div className="downloads-file-head">
                        <div className="downloads-file-icon index-file-icon">
                          <FontAwesomeIcon icon={faFile} />
                        </div>
                        <Badge className="downloads-file-badge index-file-badge">
                          {getFileExtension(file.filename)}
                        </Badge>
                      </div>
                      <h3 className="downloads-file-name" title={file.filename}>
                        {file.filename}
                      </h3>
                      <p
                        className="downloads-file-location"
                        title={getReadableLocation(file.location)}
                      >
                        {getReadableLocation(file.location)}
                      </p>
                    </Card.Body>
                    <Card.Footer className="index-file-footer">
                      <div className="index-file-actions">
                        <Button
                          variant="primary"
                          className="downloads-file-btn"
                          onClick={() => handleDownload(file)}
                          disabled={downloadingFile === file.filename}
                          title={`Download ${file.filename}`}
                        >
                          {downloadingFile === file.filename ? (
                            <Spinner
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                          ) : (
                            <FontAwesomeIcon
                              icon={faCloudArrowDown}
                              className="me-2"
                            />
                          )}
                          Download
                        </Button>
                        <Button
                          variant="outline-primary"
                          className="index-file-secondary"
                          onClick={() => handleNavigate(file.location)}
                          title="View folder"
                        >
                          <FontAwesomeIcon
                            icon={faFolderOpen}
                            className="me-2"
                          />
                          View folder
                        </Button>
                      </div>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Alert variant="light" className="downloads-empty">
              {searchActive
                ? "No files match your search right now. Try another keyword or clear the filter."
                : "We haven’t indexed any files yet. Upload files to see them appear here automatically."}
            </Alert>
          )}

          {rawTotalPages > 1 && (
            <div className="index-pagination">
              <Pagination className="index-pagination-control">
                <Pagination.First
                  disabled={safePage === 1}
                  onClick={() => setCurrentPage(1)}
                />
                <Pagination.Prev
                  disabled={safePage === 1}
                  onClick={() =>
                    setCurrentPage((page) => Math.max(1, page - 1))
                  }
                />
                {paginationItems.map((item, index) => {
                  if (item.type === "ellipsis") {
                    return (
                      <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                    );
                  }
                  return (
                    <Pagination.Item
                      key={`page-${item.page}`}
                      active={item.page === safePage}
                      onClick={() => setCurrentPage(item.page)}
                    >
                      {item.page}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next
                  disabled={safePage === rawTotalPages}
                  onClick={() =>
                    setCurrentPage((page) => Math.min(rawTotalPages, page + 1))
                  }
                />
                <Pagination.Last
                  disabled={safePage === rawTotalPages}
                  onClick={() => setCurrentPage(rawTotalPages)}
                />
              </Pagination>
              <div className="index-pagination-status">
                Page {safePage} of {rawTotalPages}
              </div>
            </div>
          )}
        </section>
      </Container>

      <Modal
        show={showQuickSearch}
        onHide={() => setShowQuickSearch(false)}
        centered
        size="lg"
        fullscreen="md-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>Quick file search</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FileIndexSearch />
        </Modal.Body>
      </Modal>
    </div>
  );
}
